/**
 * All supported markdown inline markers for text styling.
 * Ordered from longest to shortest for proper matching priority.
 */
export const ALL_MARKERS = ['~~', '**', '__', '*', '_', '`'] as const

export type Marker = (typeof ALL_MARKERS)[number]

/**
 * Characters that can be used as part of markdown inline markers.
 */
const MARKER_CHARS = new Set(['*', '_', '~', '`'])

const isAlphanumeric = (char: string | undefined): boolean => {
  return !!char && /^[a-zA-Z0-9]$/.test(char)
}

/**
 * Returns alternative markers for a given wrapper.
 * Supports _ vs * for italic, __ vs ** for bold.
 * Strike (~~) and code (`) have no alternatives.
 */
export const getVariants = (wrapper: string): string[] => {
  if (wrapper === '*' || wrapper === '_') return ['*', '_']
  if (wrapper === '**' || wrapper === '__') return ['**', '__']
  return [wrapper]
}

/**
 * Finds the inner text selection by skipping marker characters from both ends.
 * This identifies the actual text content that will be marked/unmarked.
 *
 * @param text - The selected text
 * @returns Object with start and end positions of inner text relative to input
 */
export const findInnerText = (text: string): { start: number; end: number } => {
  let start = 0
  let end = text.length

  while (start < end && MARKER_CHARS.has(text[start])) {
    start++
  }

  while (end > start && MARKER_CHARS.has(text[end - 1])) {
    end--
  }

  return { start, end }
}

/**
 * Toggles a marking in a list of markings.
 * If any variant of the marking exists, removes all occurrences.
 * Otherwise, adds the marking to the beginning (innermost position, closest to text).
 *
 * @param markings - Current list of markings (inside to outside)
 * @param marker - The marker to toggle
 * @returns Updated list of markings
 */
export const toggleMarking = (markings: Marker[], marker: Marker): Marker[] => {
  const variants = getVariants(marker) as Marker[]

  const hasVariant = markings.some((m) => variants.includes(m))

  if (hasVariant) {
    return markings.filter((m) => !variants.includes(m))
  } else {
    return [marker, ...markings]
  }
}

/**
 * Reads enclosing markers from text surrounding the selection.
 * Returns markers in order from innermost to outermost.
 *
 * @param text - The full text
 * @param selStart - Selection start position
 * @param selEnd - Selection end position
 * @returns Object with markers array and positions of the enclosing marker area
 */
export const readEnclosingMarkers = (
  text: string,
  selStart: number,
  selEnd: number,
): { markers: Marker[]; enclosingStart: number; enclosingEnd: number } => {
  const markers: Marker[] = []

  let leftPos = selStart
  let rightPos = selEnd

  while (leftPos > 0 && rightPos < text.length) {
    let foundMarker: Marker | null = null

    for (const marker of ALL_MARKERS) {
      const leftStart = leftPos - marker.length
      if (leftStart < 0) continue

      const leftPart = text.slice(leftStart, leftPos)
      const rightPart = text.slice(rightPos, rightPos + marker.length)

      if (leftPart === marker && rightPart === marker) {
        foundMarker = marker
        break
      }
    }

    if (foundMarker) {
      // Special case: underscores that are part of words (snake_case) are not markers
      if (foundMarker === '_' || foundMarker === '__') {
        const charBefore = text[leftPos - foundMarker.length - 1]
        const charAfter = text[rightPos + foundMarker.length]
        if (isAlphanumeric(charBefore) && isAlphanumeric(charAfter)) {
          break
        }
      }

      markers.push(foundMarker)
      leftPos -= foundMarker.length
      rightPos += foundMarker.length
    } else {
      break
    }
  }

  return {
    markers,
    enclosingStart: leftPos,
    enclosingEnd: rightPos,
  }
}

/**
 * Builds the new text with updated markings around the inner text.
 *
 * @param text - The full text
 * @param enclosingStart - Start position of area including all markers
 * @param enclosingEnd - End position of area including all markers
 * @param innerText - The actual text content without markers
 * @param newMarkings - New markings to apply (inside to outside)
 * @returns Object with new value and updated selection positions (at inner text)
 */
export const buildMarkedText = (
  text: string,
  enclosingStart: number,
  enclosingEnd: number,
  innerText: string,
  newMarkings: Marker[],
): { value: string; start: number; end: number } => {
  const beforeArea = text.slice(0, enclosingStart)
  const afterArea = text.slice(enclosingEnd)

  const leftMarking = [...newMarkings].reverse().join('')
  const rightMarking = newMarkings.join('')

  const newContent = leftMarking + innerText + rightMarking

  const value = beforeArea + newContent + afterArea

  const start = enclosingStart + leftMarking.length
  const end = start + innerText.length

  return { value, start, end }
}

/**
 * Wraps or unwraps text with markdown inline markers (bold, italic, strike, code).
 *
 * This function handles:
 * - Basic wrapping: adds markers around the selection
 * - Basic unwrapping: removes markers when selection includes them
 * - Enclosing unwrapping: removes markers when they surround the selection
 * - Nested unwrapping: handles complex nesting like bold+italic+strike
 * - Alternative markers: recognizes both * and _ for italic, ** and __ for bold
 * - Special case: underscores that are part of words (e.g., snake_case) are not treated as markers
 *
 * @param text - The full text content
 * @param selStart - Selection start position
 * @param selEnd - Selection end position
 * @param wrapper - The marker to toggle ('*', '**', '~~', '`')
 * @returns Object with new text value and updated selection positions (always at inner text)
 */
export const wrapOrUnwrap = (
  text: string,
  selStart: number,
  selEnd: number,
  wrapper: string,
): { value: string; start: number; end: number } => {
  const selected = text.slice(selStart, selEnd)

  const inner = findInnerText(selected)
  const innerAbsStart = selStart + inner.start
  const innerAbsEnd = selStart + inner.end

  const enclosing = readEnclosingMarkers(text, innerAbsStart, innerAbsEnd)

  const innerText = selected.slice(inner.start, inner.end)

  const newMarkings = toggleMarking(enclosing.markers, wrapper as Marker)

  return buildMarkedText(
    text,
    enclosing.enclosingStart,
    enclosing.enclosingEnd,
    innerText,
    newMarkings,
  )
}
